import { ReactNode } from "react";
import { Menu, Plus, Calendar, TrendingUp } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { MiniTimer } from "@/components/header/MiniTimer";
import { OfflineIndicator } from "@/components/pwa";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useDashboardPremium } from "@/hooks/useDashboardPremium";

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

// Quick action config per route
const quickActions: Record<string, { label: string; path: string; icon?: boolean }> = {
  '/dashboard': { label: 'Nova Entrada', path: '/timer', icon: true },
  '/projects': { label: 'Novo Projeto', path: '/projects', icon: true },
  '/clients': { label: 'Novo Cliente', path: '/clients', icon: true },
  '/timer': { label: 'Entrada Manual', path: '/timer', icon: true },
  '/payments': { label: 'Novo Pagamento', path: '/payments', icon: true },
};

export function DashboardLayout({ children, title, subtitle }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { goalProgress, confirmedRevenue, monthlyGoal } = useDashboardPremium();

  const currentMonth = format(new Date(), "MMMM 'de' yyyy", { locale: ptBR });
  const currentMonthShort = format(new Date(), "MMM yyyy", { locale: ptBR });
  
  const quickAction = quickActions[location.pathname];
  const showGoalBadge = location.pathname === '/dashboard' && monthlyGoal > 0;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-muted/30">
        <AppSidebar />
        <div className="flex flex-1 flex-col">
          {/* Premium Header */}
          <header className={cn(
            "sticky top-0 z-40",
            "flex h-[4.5rem] items-center justify-between",
            "bg-card/95 backdrop-blur-xl",
            "px-4 lg:px-8",
            "border-b border-border/30",
            "shadow-[0_1px_3px_0_rgba(0,0,0,0.03)]",
            "transition-all duration-200"
          )}>
            <div className="flex items-center gap-4">
              {/* Mobile Menu Trigger */}
              <SidebarTrigger className="lg:hidden">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-9 w-9 hover:bg-muted/80 rounded-xl transition-colors duration-200"
                >
                  <Menu className="h-5 w-5 text-muted-foreground" />
                </Button>
              </SidebarTrigger>

              {/* Title Area */}
              <div className="flex flex-col gap-0.5">
                <h1 className="text-lg font-semibold tracking-tight text-foreground leading-tight">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-xs text-muted-foreground/70 leading-tight hidden sm:block">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Right Side - Context + Actions */}
            <div className="flex items-center gap-3">
              {/* Context Badge - Current Period */}
              <div className="hidden md:flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  <span className="capitalize">{currentMonthShort}</span>
                </div>

                {/* Goal Progress Badge */}
                {showGoalBadge && goalProgress > 0 && (
                  <div className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200",
                    goalProgress >= 100 
                      ? "bg-success/10 text-success" 
                      : goalProgress >= 50 
                        ? "bg-primary/10 text-primary"
                        : "bg-warning/10 text-warning"
                  )}>
                    <TrendingUp className="h-3.5 w-3.5" />
                    <span>{Math.round(goalProgress)}% da meta</span>
                  </div>
                )}
              </div>

              {/* Offline Status Indicator */}
              <OfflineIndicator />

              {/* Divider */}
              <div className="hidden md:block h-6 w-px bg-border/50" />
              {quickAction && (
                <Button
                  size="sm"
                  className={cn(
                    "h-8 px-3 rounded-2xl gap-1.5",
                    "bg-primary hover:bg-primary/90",
                    "text-xs font-medium",
                    "shadow-sm hover:shadow-md",
                    "transition-all duration-200"
                  )}
                  onClick={() => navigate(quickAction.path)}
                >
                  {quickAction.icon && <Plus className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{quickAction.label}</span>
                  <span className="sm:hidden">Novo</span>
                </Button>
              )}

              {/* Mini Timer */}
              <MiniTimer />
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-8">
            <div className="animate-fade-in">
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
